import { ReactNode, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import StatStrip from "../../../Component/StatStrip";
import Pill from "../../../Component/Pill";
import InvoiceDrawer from "../../Invoices/components/InvoiceDrawer";
import TablePager from "./TablePager";
import { getRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import { ApiInvoice, inr2, invoiceNo, invoiceStatus, invoiceTotals } from "../../Invoices/invoiceUtils";

interface CustomerWalletTabProps {
  userId: string;
  walletBalance: number;
  creditLine: boolean;
  // bump to reload after a wallet adjustment
  reloadKey?: number;
  // buttons in the card header (reconcile, meta pricing, adjust wallet)
  actions?: ReactNode;
}

// Customer details → Wallet tab: this customer's wallet_topup invoices (GET /subscription/walletTopups?userId=)
const CustomerWalletTab = ({ userId, walletBalance, creditLine, reloadKey, actions }: CustomerWalletTabProps) => {
  const [topupList, setTopupList] = useState<ApiInvoice[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalTopups, setTotalTopups] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stats, setStats] = useState({ credited: 0, collected: 0, count: 0 });

  // Filters
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  const [openInvoice, setOpenInvoice] = useState<ApiInvoice | null>(null);

  // Fetch Wallet Top-ups (all time)
  const getTopupList = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getRequest(
        `/subscription/walletTopups?page=${page}&limit=${pageSize}&userId=${userId}&invoiceStatus=${statusFilter}&search=${encodeURIComponent(searchQuery)}&sort=${sortOrder}`
      );

      if (response.status === 'success') {
        setTopupList(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalTopups(response.pagination.total);
        if (response.stats) {
          setStats({
            credited: response.stats.credited.value,
            collected: response.stats.collected.value,
            count: response.stats.count.value,
          });
        }
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading wallet top-ups!");
    } finally {
      setIsLoading(false);
    }
  }, [userId, page, pageSize, statusFilter, searchQuery, sortOrder]);

  useEffect(() => {
    getTopupList();
  }, [getTopupList, reloadKey]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const resetFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setSortOrder("newest");
    setPage(1);
  };

  const lowBalance = Number(walletBalance || 0) < 500;

  return (
    <>
      <StatStrip items={[
        { label: "Wallet balance", value: inr2(walletBalance), icon: "ri-wallet-3-line", delta: lowBalance ? "low balance" : creditLine ? "credit line enabled" : "prepaid wallet" },
        { label: "Credited to wallet", value: inr2(stats.credited), icon: "ri-add-circle-line", delta: "paid top-ups · all time" },
        { label: "Collected (incl. GST)", value: inr2(stats.collected), icon: "ri-money-rupee-circle-line", delta: "paid top-ups · all time" },
        { label: "Top-ups", value: stats.count, icon: "ri-file-list-3-line", delta: "paid · all time" },
      ]} />

      <div className="cx-card">
        <div className="cx-card-h">
          <h6>Wallet top-ups</h6>
          {actions && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>}
        </div>

        <div className="cx-toolbar">
          <select className="cx-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All status</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
          <select className="cx-select" value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}>
            <option value="newest">Sort: Newest</option>
            <option value="oldest">Sort: Oldest</option>
          </select>
          <div className="sp"></div>
          <input className="cx-input" type="search" placeholder="Invoice no / description…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
            <i className="ri-refresh-line"></i>Reset
          </button>
        </div>

        <div className="cx-tbl-wrap">
          <table className="cx-tbl">
            <thead>
              <tr>
                <th>Date &amp; time</th><th>Invoice no.</th><th>Description</th><th>Payment</th><th>Status</th>
                <th className="r">Credited</th><th className="r">GST</th><th className="r">Total paid</th><th className="r">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && !topupList.length ? (
                <tr><td colSpan={9}><div className="cx-empty"><i className="ri-loader-4-line"></i>Loading top-ups…</div></td></tr>
              ) : topupList.length ? topupList.map(i => {
                const t = invoiceTotals(i);
                const [tone, statusLabel] = invoiceStatus(i);
                return (
                  <tr key={i._id} className="click" onClick={() => setOpenInvoice(i)}>
                    <td className="fs12 muted" style={{ whiteSpace: "nowrap" }}>{i.createdAt ? moment(i.createdAt).format("DD MMM YYYY, HH:mm") : "—"}</td>
                    <td className="mono fs12 fw6">{invoiceNo(i)}</td>
                    <td className="fs12">{i.invoice_plan_name || "Wallet top-up"}</td>
                    <td className="fs12" style={{ textTransform: "uppercase" }}>{i.invoice_transaction_id?.method || <span className="muted">—</span>}</td>
                    <td><Pill tone={tone}>{statusLabel}</Pill></td>
                    <td className="mono up r">+{inr2(t.sub)}</td>
                    <td className="mono muted r">{inr2(t.gst)}</td>
                    <td className="mono fw6 r">{inr2(t.tot)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="cx-ra">
                        <button type="button" className="v" title="View invoice" onClick={() => setOpenInvoice(i)}><i className="ri-eye-line"></i></button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={9}>
                    <div className="cx-empty">
                      <i className="ri-wallet-3-line"></i>
                      {searchQuery || statusFilter !== "all"
                        ? <>No top-ups match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button></>
                        : "No wallet top-ups for this customer yet."}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePager
          page={page}
          totalPages={totalPages}
          total={totalTopups}
          shown={topupList.length}
          pageSize={pageSize}
          label="top-ups"
          note="Credited = invoice taxable value (GST is on the invoice)"
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </div>

      <InvoiceDrawer invoice={openInvoice} onClose={() => setOpenInvoice(null)} hideCustomer />
    </>
  );
};

export default CustomerWalletTab;
